import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Divider,
  Tabs,
  Spin,
  Input,
} from "antd";
import {
  CreditCardOutlined,
  AmazonOutlined,
  AppleOutlined,
  GoogleOutlined,
  DollarOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import CurrencyFormat from "react-currency-format";

import Modal from "../../components/Modal";
import useIsMobile from "../../hooks/useIsMobile";

import {
  ADD_PAYMENT_CHARGE,
  postData,
  getData,
  GET_SUBSCRIPTION_PLAN_URL,
  GET_PROMO_CODE_URL,
} from "../../utils/api";

const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#32325d",
      fontFamily: "Arial, sans-serif",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a" },
  },
  // Hide Stripe Link autofill chip
  disableLink: true,
};

const NUMBER_ELEMENT_OPTIONS = {
  ...ELEMENT_OPTIONS,
  showIcon: true,
  iconStyle: "solid",
};

const tabPaneStyle = {};

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Email validation function
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// US States list
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri",
  "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina",
  "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

const paymentMethods = [
  {
    name: "Credit Card",
    icon: <CreditCardOutlined style={{ fontSize: 24 }} />,
    key: "credit",
  },
  {
    name: "Google Pay",
    icon: <CreditCardOutlined style={{ fontSize: 24 }} />,
    key: "gpay",
  },
  {
    name: "PayPal",
    icon: <CreditCardOutlined style={{ fontSize: 24 }} />,
    key: "paypal",
  },
];

const Subscription = (props) => {
  const stripe = useStripe();
  const elements = useElements();
  const isMobile = useIsMobile();

  const { setActiveTab } = props;

  const [planType, setPlanType] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeDetails, setPromoCodeDetails] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canUsePaymentRequest, setCanUsePaymentRequest] = useState(false);

  const [open, setOpen] = useState(false);
  const [isPayClicked, setIsPayClicked] = useState(false);
  const [activeFormTab, setActiveFormTab] = useState(0); // 0: Customer Info, 1: Payment

  // Customer information fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [country] = useState("US"); // Hardcoded as US

  const getSubscriptionPlan = async () => {
    try {
      const data = await getData(GET_SUBSCRIPTION_PLAN_URL, {});
      setIsLoading(false);
      setPlans(data.list);
    } catch (e) {
      console.log("ERROR", e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSubscriptionPlan();
  }, []);

  const getPromoCode = async () => {
    try {
      const data = await getData(GET_PROMO_CODE_URL, { promocode: promoCode });
      setIsLoading(false);
      setPromoCodeDetails(data);
    } catch (e) {
      setPromoCodeDetails({
        isActive: false,
      });
      console.log("ERROR", e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Total",
          amount: 1000, // $10.00
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
          setCanUsePaymentRequest(true);
        }
      });

      pr.on("paymentmethod", async (ev) => {
        setMessage(`Digital PaymentMethod created: ${ev.paymentMethod.id}`);
        ev.complete("success");
      });
    }
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsPayClicked(true);
    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
    });
    if (error) {
      setMessage(error.message);
      setIsPayClicked(false);
    } else {
      try {
        const response = await postData(ADD_PAYMENT_CHARGE, {
          paymentId: paymentMethod.id,
          modelId: planType,
          promocode: promoCodeDetails?.code || "",
          card: paymentMethod.card.last4,
          brand: paymentMethod.card.brand,
          expiry:
            paymentMethod.card.exp_month + "/" + paymentMethod.card.exp_year,
          fullName: fullName,
          email: email,
          addressLine1: addressLine1,
          addressLine2: addressLine2,
          city: city,
          state: state,
          pincode: pincode,
          country: country,
        });
        setIsPayClicked(false);
        if (response?.message === "updated.successfully") {
          setActiveTab(1);
        }
      } catch (error) {
        setIsPayClicked(false);
        // Show error message from backend API
        if (error.response?.data?.message) {
          setMessage(error.response.data.message);
        } else if (error.response?.data?.error) {
          setMessage(error.response.data.error);
        } else if (error.message) {
          setMessage(error.message);
        } else {
          setMessage("Payment failed. Please try again.");
        }
      }
    }
  };

  const showModal = () => {
    setOpen(true);
    setMessage(""); // Clear any previous error messages
    setActiveFormTab(0); // Reset to first tab
    // Clear customer information fields
    setFullName("");
    setEmail("");
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setState("");
    setPincode("");
  };

  const handleCancel = () => {
    console.log("Clicked cancel button");
    setOpen(false);
    setMessage(""); // Clear error messages when closing
  };

  const applyDiscount = () => {
    if (promoCodeDetails.isPercentage) {
      return (
        selectedPlan.finalPrice -
        (selectedPlan.finalPrice * promoCodeDetails.value) / 100
      );
    } else {
      return selectedPlan.finalPrice - promoCodeDetails.value;
    }
  };

  return (
    <>
      <Spin spinning={isLoading} tip="Loading...">
        <Row gutter={isMobile ? [16, 16] : [24, 32]} style={{ marginTop: 20 }}>
          {plans.map((plan, index) => (
            <Col key={index} xs={24} sm={12} md={8} lg={6}>
              <Card
                className="subscription-card"
                title={
                  <div>
                    <Title style={{ margin: 0 }} level={4}>
                      {plan.planName}
                    </Title>
                    <span style={{ fontSize: 14, color: "#8c8c8c" }}>
                      {plan.noOfLookups > 0 ? plan.description : "-"}
                    </span>
                  </div>
                }
                bordered
                style={{
                  border:
                    planType === plan.type ? "2px solid #1890ff" : undefined,
                  borderRadius: 16,
                  minHeight: 320,
                }}
              >
                <div className="subscription-card-content">
                  <Text type="secondary">
                    {plan.noOfLookups > 0
                      ? `${plan.noOfLookups} Lookups`
                      : plan.description}
                  </Text>
                  {plan.finalPrice > 0 && (
                    <>
                      <Title level={2} style={{ marginTop: 16 }}>
                        <CurrencyFormat
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                          value={plan.finalPrice}
                          style={{
                            fontSize: "22px",
                          }}
                        />
                        <CurrencyFormat
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                          value={plan.originalPrice}
                          style={{
                            fontSize: "16px",
                            color: "lightgrey",
                            paddingLeft: "12px",
                            textDecoration: "line-through",
                          }}
                        />
                        <CurrencyFormat
                          displayType={"text"}
                          thousandSeparator={true}
                          value={(
                            ((plan.originalPrice - plan.finalPrice) /
                              plan.originalPrice) *
                            100
                          ).toFixed(2)}
                          suffix={"% off"}
                          style={{
                            fontSize: "16px",
                            color: "green",
                            paddingLeft: "12px",
                          }}
                        />
                      </Title>

                      <Divider style={{ borderColor: "lightgray" }} />
                    </>
                  )}
                </div>

                {plan.finalPrice > 0 && (
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={() => {
                      setPlanType(plan.modelId);
                      setSelectedPlan(plan);
                      showModal();
                    }}
                  >
                    Buy
                  </Button>
                )}
                {plan.finalPrice === 0 && (
                  <>
                    <Title level={2} style={{ marginTop: 16 }}>
                      <CurrencyFormat
                        displayType={"text"}
                        thousandSeparator={true}
                        value={"-"}
                        style={{
                          fontSize: "22px",
                        }}
                      />
                    </Title>

                    <Divider style={{ borderColor: "lightgray" }} />

                    <Button
                      type="primary"
                      size="large"
                      block
                      onClick={() => {}}
                    >
                      Call Us
                    </Button>
                  </>
                )}
              </Card>
            </Col>
          ))}
        </Row>

        {open && (
          <Modal>
            <div className="payment-modal">
              <div className="payment-modal-header">
                <div className="pm-header-main">
                  <div className="pm-header-left">
                    <div className="pm-plan-title">{selectedPlan.planName}</div>
                    <div className="pm-plan-sub">
                      {`${selectedPlan.noOfLookups} Lookups`}
                      {selectedPlan?.description ? ` • ${selectedPlan.description}` : ""}
                    </div>
                  </div>
                  <div className="pm-header-right">
                    {promoCodeDetails && promoCodeDetails?.isActive ? (
                      <>
                        <div className="pm-price-current">
                          <CurrencyFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"$"}
                            value={applyDiscount()}
                          />
                        </div>
                        <div className="pm-price-old">
                          <CurrencyFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"$"}
                            value={selectedPlan.finalPrice}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="pm-price-current">
                        <CurrencyFormat
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                          value={selectedPlan.finalPrice}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h1
                    className="payment-modal-close"
                    onClick={() => {
                      setOpen(false);
                      setPlanType("");
                      setSelectedPlan(null);
                      setMessage(""); // Clear error messages when closing
                    }}
                  >
                    {" "}
                    X{" "}
                  </h1>
                </div>
              </div>
              <Card className="payment-modal-card">
                {/* {promoCodeDetails && !promoCodeDetails?.isActive && (
                  <div style={{ color: "red" }}>Invalid Promo Code</div>
                )}
                {promoCodeDetails && promoCodeDetails?.isActive && (
                  <div style={{ color: "green" }}>Promo Code Applied</div>
                )} */}
                <div className="promo-row">
                  <Input
                    placeholder="Apply Promocode"
                    size="large"
                    className={
                      promoCodeDetails && promoCodeDetails?.isActive
                        ? "success-input"
                        : ""
                    }
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCodeDetails(null);
                      setPromoCode(e.target.value);
                    }}
                    status={
                      promoCodeDetails && !promoCodeDetails?.isActive
                        ? "error"
                        : ""
                    }
                    disabled={promoCodeDetails && promoCodeDetails?.isActive}
                  />
                  <Button
                    type="primary"
                    className="promo-apply-btn"
                    disabled={!promoCode}
                    onClick={() => {
                      if (promoCodeDetails && promoCodeDetails?.isActive) {
                        setPromoCodeDetails(null);
                        setPromoCode("");
                      } else {
                        getPromoCode();
                      }
                    }}
                  >
                    {promoCodeDetails && promoCodeDetails?.isActive
                      ? "Clear"
                      : "Apply"}
                  </Button>
                </div>
                <div className="form-tabs-container">
                  <div className="form-tabs">
                    <div 
                      className={`form-tab ${activeFormTab === 0 ? 'active' : ''}`}
                      onClick={() => setActiveFormTab(0)}
                    >
                      Customer Info
                    </div>
                    <div 
                      className={`form-tab ${activeFormTab === 1 ? 'active' : ''}`}
                      onClick={() => setActiveFormTab(1)}
                    >
                      Payment
                    </div>
                  </div>

                  <div className="form-tab-content">
                    {activeFormTab === 0 ? (
                      <div className="customer-info-tab">
                        <div className="form-section">
                          <h4 className="section-title">Email</h4>
                          <input
                            type="email"
                            className={`customer-input ${email && !isValidEmail(email) ? 'input-error' : ''}`}
                            placeholder="email@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                          />
                        </div>

                        <div className="form-section">
                          <h4 className="section-title">Shipping address</h4>
                          <div className="address-grid">
                            <div className="address-field">
                              <input
                                type="text"
                                className="customer-input"
                                placeholder="Full name"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                required
                              />
                            </div>
                            <div className="address-field">
                              <input
                                type="text"
                                className="customer-input"
                                placeholder="Address line"
                                value={addressLine1}
                                onChange={(e) => setAddressLine1(e.target.value)}
                                required
                              />
                            </div>
                            <div className="address-field">
                              <input
                                type="text"
                                className="customer-input"
                                placeholder="City"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                required
                              />
                            </div>
                            <div className="address-field">
                              <select
                                className="customer-input"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                required
                              >
                                <option value="">Select State</option>
                                {US_STATES.map((stateName) => (
                                  <option key={stateName} value={stateName}>
                                    {stateName}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="address-field">
                              <input
                                type="text"
                                className="customer-input"
                                placeholder="ZIP Code"
                                value={pincode}
                                onChange={(e) => {
                                  // Only allow numbers
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  setPincode(value);
                                }}
                                required
                                pattern="[0-9]+"
                                maxLength="10"
                              />
                            </div>
                            <div className="address-field">
                              <input
                                type="text"
                                className="customer-input"
                                placeholder="United States"
                                value="United States"
                                disabled
                              />
                            </div>
                          </div>
                        </div>

                        <div className="tab-navigation">
                          <button 
                            type="button"
                            className="next-tab-btn"
                            onClick={() => setActiveFormTab(1)}
                            disabled={!fullName || !email || !addressLine1 || !city || !state || !pincode || !isValidEmail(email) || pincode.length < 5}
                          >
                            Continue to Payment
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="payment-tab">
                        <form
                          onSubmit={handleSubmit}
                          className="checkout-form"
                          autoComplete="off"
                        >
                          <label>Card Number</label>
                          <div className="card-input card-number-input">
                            <CardNumberElement
                              options={NUMBER_ELEMENT_OPTIONS}
                              onChange={() => setMessage("")}
                            />
                          </div>

                          <label>Expiration Date</label>
                          <div className="card-input card-expiry-input">
                            <CardExpiryElement
                              options={ELEMENT_OPTIONS}
                              onChange={() => setMessage("")}
                            />
                          </div>

                          <label>CVC</label>
                          <div className="card-input card-cvc-input">
                            <CardCvcElement
                              options={ELEMENT_OPTIONS}
                              onChange={() => setMessage("")}
                            />
                            <span className="cvc-icon">123</span>
                          </div>

                          <div className="tab-navigation">
                            <button 
                              type="button"
                              className="prev-tab-btn"
                              onClick={() => setActiveFormTab(0)}
                            >
                              Back to Customer Info
                            </button>
                            <button
                              type="submit"
                              className="pay-btn"
                              disabled={!stripe || isPayClicked || isLoading}
                            >
                              {!isPayClicked && !isLoading ? "Pay" : "Loading"}
                            </button>
                          </div>

                          {message && (
                            <div className="message">{message}</div>
                          )}
                          <div className="powered-by-stripe">
                            <span>Powered by </span>
                            <span className="stripe-wordmark">stripe</span>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>

              </Card>
            </div>
          </Modal>
        )}

        {/* {planType && (
        <>
          <div style={{ width: "100%", marginTop: 40 }}>
            <Row gutter={24}>
              {paymentMethods.map((method) => (
                <Col xs={24} sm={12} md={8} key={method.key}>
                  <Card
                    hoverable
                    onClick={() => setSelected(method.key)}
                    style={{
                      border:
                        selected === method.key
                          ? "2px solid #1890ff"
                          : undefined,
                      textAlign: "center",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ marginBottom: 8 }}>{method.icon}</div>
                    <strong>{method.name}</strong>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
          {selected === "credit" && (
            <div style={{ width: "100%", marginTop: 40 }}>
              <Row gutter={24}>
                <form onSubmit={handleSubmit} className="checkout-form">
                  <label>Card Number</label>
                  <div className="card-input">
                    <CardNumberElement options={ELEMENT_OPTIONS} />
                  </div>

                  <label>Expiration Date</label>
                  <div className="card-input">
                    <CardExpiryElement options={ELEMENT_OPTIONS} />
                  </div>

                  <label>CVC</label>
                  <div className="card-input">
                    <CardCvcElement options={ELEMENT_OPTIONS} />
                  </div>

                  <button type="submit" disabled={!stripe}>
                    Pay
                  </button>

                  {message && <div className="message">{message}</div>}
                </form>
              </Row>
            </div>
          )}
        </>
      )} */}
      </Spin>
    </>
  );
};

export default Subscription;
